import { useEffect, useState } from 'react';
import { useT } from '@/store';
import { useDrivers } from '@/hooks/useDirectory';
import { ALL_STATUSES } from '@/lib/format';

/**
 * Filter bar for the orders table. Every filter is applied server-side (see
 * GET /api/orders), so this component's only job is to produce a clean
 * filter object and to keep the search box from firing a request per
 * keystroke.
 *
 * The search input is locally controlled and debounced 300 ms before it
 * reaches the parent — typing a six-character waybill number should cost one
 * query, not six.
 */
export function OrderFilters({ filters, onChange }) {
  const t = useT();
  // V-368. includeInactive, unlike every other useDrivers() caller: this is
  // the ONLY driver filter in the product (grep: `orders.allDrivers` has one
  // reader), and the table it filters deliberately keeps showing a departed
  // driver's loads — GET /api/orders filters on o.driver_id and never joins
  // on drivers.active. Reading the active roster meant the one screen that
  // can answer "which loads did the driver who left run" could not be asked
  // the question at all. The archived entries are marked the way DriverList
  // marks them; an <option> cannot hold ArchivedBadge, so it carries the
  // badge's own words instead of a second wording.
  const { data: drivers = [] } = useDrivers({ includeInactive: true });
  const [search, setSearch] = useState(filters.search ?? '');

  useEffect(() => {
    if (search === (filters.search ?? '')) return undefined;
    const timer = window.setTimeout(() => onChange({ search, page: 1 }), 300);
    return () => window.clearTimeout(timer);
  }, [search, filters.search, onChange]);

  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
      <input
        className="field lg:col-span-2"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('orders.searchPh')}
        type="search"
      />

      <select
        className="field"
        value={filters.status ?? ''}
        onChange={(e) => onChange({ status: e.target.value, page: 1 })}
      >
        <option value="">{t('orders.filterAll')}</option>
        <option value="active">{t('orders.filterActive')}</option>
        {ALL_STATUSES.map((status) => (
          <option key={status} value={status}>
            {t(`status.${status}`)}
          </option>
        ))}
      </select>

      <select
        className="field"
        value={filters.driverId ?? ''}
        onChange={(e) => onChange({ driverId: e.target.value, page: 1 })}
      >
        <option value="">{t('orders.allDrivers')}</option>
        {drivers.map((d) => (
          <option key={d.driver_id} value={d.driver_id}>
            {d.active ? d.full_name : `${d.full_name} (${t('common.archived')})`}
          </option>
        ))}
      </select>

      <div className="flex gap-2">
        <input
          className="field"
          type="date"
          value={filters.dateFrom ?? ''}
          onChange={(e) => onChange({ dateFrom: e.target.value, page: 1 })}
          aria-label={t('orders.dateFrom')}
        />
        <input
          className="field"
          type="date"
          value={filters.dateTo ?? ''}
          onChange={(e) => onChange({ dateTo: e.target.value, page: 1 })}
          aria-label={t('orders.dateTo')}
        />
      </div>
    </div>
  );
}
